import { adminApi, adminApiInZone } from './api-client';
import type { PortalZone } from './zones';

/**
 * Route one call to the domain's own zone (ADR-122 Pattern 2). Omitting the zone means HOME.
 *
 * A domain belongs to an account, which lives in exactly one zone — so these calls follow the
 * `?zone=` that `DomainDetailPage` reads off its URL, the same passthrough shape
 * `AdminCustomerDetailPage` already uses.
 */
const call = <T>(zone: PortalZone | undefined, path: string, init?: RequestInit): Promise<T> =>
  zone ? adminApiInZone<T>(zone, path, init) : adminApi<T>(path, init);

/**
 * P48 (punchlist 8, 14 June 2026) — typed wrappers around the admin-api staff
 * Fix Card surfaces (lambdas/admin-api/src/handlers.ts listFixCards /
 * setFixCardBeta):
 *
 *   - GET   .../domains/{domainToken}/fix-guide/cards        → list (read).
 *   - PATCH .../domains/{domainToken}/fix-guide/cards/{ref}/beta → toggle the
 *     beta/test marker (MUTATING_ROLES, fresh-auth).
 *
 * Both are gated server-side; the portal gate is convenience, never the sole
 * control. The card title is not stored on the workflow row, so `label` is the
 * matchKey's metric segment — enough for staff to recognise the card.
 */

export interface StaffFixCard {
  ref: string;
  label: string;
  status: string;
  priority: string;
  beta: boolean;
}

export async function listFixCards(
  accountId: string,
  domainToken: string,
  /** The domain's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<StaffFixCard[]> {
  const data = await call<{ domainToken: string; cards: StaffFixCard[] }>(zone,
    `/admin/customers/${encodeURIComponent(accountId)}/domains/${encodeURIComponent(domainToken)}/fix-guide/cards`,
  );
  return data.cards;
}

export async function setFixCardBeta(
  accountId: string,
  domainToken: string,
  ref: string,
  beta: boolean,
  /** The domain's own zone. Omitted = the home zone. */
  zone?: PortalZone,
): Promise<{ ref: string; beta: boolean }> {
  return call<{ ref: string; beta: boolean }>(zone,
    `/admin/customers/${encodeURIComponent(accountId)}/domains/${encodeURIComponent(domainToken)}/fix-guide/cards/${encodeURIComponent(ref)}/beta`,
    { method: 'PATCH', body: JSON.stringify({ beta }) },
  );
}
